import React from 'react';
import { Button, Typography } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import styled from 'styled-components/macro';
import TabToolbar from '../TabToolbar';
import { SearchBar } from '../../../../../search/SearchBar';
import { useEntityRegistry } from '../../../../../useEntityRegistry';
import { EntityType, AndFilterInput, SearchAcrossEntitiesInput } from '../../../../../../types.generated';
import { useTranslation } from '../../../../../../i18n/useTranslation';
import SearchExtendedMenu from './SearchExtendedMenu';
import { SearchSelectBar } from './SearchSelectBar';
import { EntityAndType } from '../../../types';
import { SearchResultsInterface } from './types';
import { useEntityData } from '../../../EntityContext';

const HeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding-right: 4px;
    padding-left: 4px;
`;

const SearchAndDownloadContainer = styled.div`
    && {
        display: flex;
        align-items: center;
    }

    .ant-input-affix-wrapper {
        background-color: #f4f6f8 !important;
    }
`;

const SearchMenuContainer = styled.div`
    margin-top: 8px;
    margin-left: 10px;
`;

type Props = {
    onSearch: (q: string) => void;
    onToggleFilters: () => void;
    placeholderText?: string | null;
    callSearchOnVariables?: (variables: {
        input: SearchAcrossEntitiesInput;
    }) => Promise<SearchResultsInterface | null | undefined>;
    filters: AndFilterInput[];
    query: string;
    isSelectMode: boolean;
    isSelectAll: boolean;
    isIndeterminate?: boolean;
    entityFilters: EntityType[];
    selectedEntities: EntityAndType[];
    setIsSelectMode: (showSelectMode: boolean) => any;
    onChangeSelectAll: (selected: boolean) => void;
    refetch?: () => void;
    searchBarStyle?: any;
    searchBarInputStyle?: any;
    totalResults?: number;
    overrideCheckDownloadType?: boolean;
};

export default function EmbeddedListSearchHeader({
    onSearch,
    onToggleFilters,
    placeholderText,
    callSearchOnVariables,
    entityFilters,
    filters,
    query,
    isSelectMode,
    isSelectAll,
    isIndeterminate,
    selectedEntities,
    setIsSelectMode,
    onChangeSelectAll,
    refetch,
    searchBarStyle,
    searchBarInputStyle,
    totalResults,
    overrideCheckDownloadType,
}: Props) {
    const entityRegistry = useEntityRegistry();
    const { t } = useTranslation();
    const { entityData } = useEntityData();
    // Only disable when explicitly set to true (null and false are treated as enabled)
    const isReadOnly = entityData?.readOnly?.readOnly === true;
    return (
        <>
            <TabToolbar>
                <HeaderContainer>
                    <Button type="default" onClick={onToggleFilters} aria-label={t('Filters')}>
                        <FilterOutlined aria-hidden="true" />
                        <Typography.Text>{t('Filters')}</Typography.Text>
                    </Button>
                    <SearchAndDownloadContainer>
                        <SearchBar
                            data-testid="embedded-search-bar"
                            initialQuery=""
                            placeholderText={placeholderText || t('Search entities')}
                            suggestions={[]}
                            style={
                                searchBarStyle || {
                                    maxWidth: 220,
                                    padding: 0,
                                }
                            }
                            inputStyle={
                                searchBarInputStyle || {
                                    height: 32,
                                    fontSize: 14,
                                }
                            }
                            onSearch={onSearch}
                            onQueryChange={onSearch}
                            entityRegistry={entityRegistry}
                            hideRecommendations
                        />
                        <SearchMenuContainer>
                            <SearchExtendedMenu
                                totalResults={totalResults}
                                callSearchOnVariables={callSearchOnVariables}
                                entityFilters={entityFilters}
                                filters={filters}
                                query={query}
                                setShowSelectMode={setIsSelectMode}
                                overrideCheckDownloadType={overrideCheckDownloadType}
                                isReadOnly={isReadOnly}
                            />
                        </SearchMenuContainer>
                    </SearchAndDownloadContainer>
                </HeaderContainer>
            </TabToolbar>
            {isSelectMode && (
                <TabToolbar>
                    <SearchSelectBar
                        isSelectAll={isSelectAll}
                        isIndeterminate={isIndeterminate}
                        onChangeSelectAll={onChangeSelectAll}
                        selectedEntities={selectedEntities}
                        onCancel={() => {
                            setIsSelectMode(false);
                        }}
                        refetch={refetch}
                    />
                </TabToolbar>
            )}
        </>
    );
}
